import { useEffect, useMemo, useState } from 'react';
import { Product } from '~/types/product';
import {
  getProductImages,
  getProductVariants,
  getSizes,
} from '../utils/product';
import { COLOR } from '../constants/product';

export const useProductDetails = (
  product: Product,
  productsByHandle: Product[],
  preselectedSize?: string,
) => {
  const initialVariant =
    product.variants?.find((variant) => variant.availableForSale)?.options ||
    product.variants[0]?.options;

  const initialColor =
    initialVariant?.length > 1
      ? initialVariant?.find(({ name }) => name === COLOR)?.value
      : '';
  const initialImage = Array.isArray(product.images) ? product.images[0] : null;
  const availableSizes = getSizes(initialColor as string, product.variants);
  const initialSize =
    availableSizes.find((size) => size?.available)?.size || '';

  const [isSizingOpen, setIsSizingOpen] = useState(false);
  const [productSettings, setProductSettings] = useState({
    selectedColor: initialColor as string,
    selectedSize: initialSize,
    selectedImage: initialImage,
  });

  const { selectedColor, selectedImage } = productSettings;

  const variants = useMemo(
    () => getProductVariants(productsByHandle),
    [productsByHandle],
  );

  const selectedProductImages = useMemo(
    () => getProductImages(product, productsByHandle, selectedColor),
    [product, productsByHandle, selectedColor],
  );

  const filteredProductImages = useMemo(() => {
    if (!selectedProductImages) return [];
    const filteredImages = selectedProductImages.filter(
      (image) => image !== selectedImage,
    );
    return filteredImages.map((image, index) => ({
      image,
      id: `product_image_${index}`,
    }));
  }, [selectedImage, selectedProductImages]);

  const toggleSizingModal = () => setIsSizingOpen((prev) => !prev);

  useEffect(() => {
    if (preselectedSize) {
      setProductSettings((prev) => ({
        ...prev,
        selectedSize: preselectedSize,
      }));
    }
  }, [preselectedSize]);

  useEffect(() => {
    const noMatchingImage = selectedProductImages?.every(
      (img) => selectedImage !== img,
    );
    if (selectedProductImages && noMatchingImage) {
      setProductSettings((prev) => ({
        ...prev,
        selectedImage: selectedProductImages[0],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductImages]);

  useEffect(() => {
    if (isSizingOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'initial';
    }
  }, [isSizingOpen]);

  return {
    isSizingOpen,
    toggleSizingModal,
    selectedImage,
    filteredProductImages,
    setProductSettings,
    productSettings,
    variants,
  };
};
